/**
 * Attempts to highlight the specified phrase inside the container.
 * @param container Container to search inside.
 * @param paragraph Paragraph to highlight.
 * @param snippet If specified, highlights only that exact match inside the paragraph (this is usually a numeric figure, such as 4%).
 * @param options.exactOnly Highlight the snippet or nothing: never the whole
 * paragraph. A table cell sets it (#12090), because its paragraph is the
 * whole source table and a value the agent reworded, converted or computed
 * is in no paragraph at all.
 */
export function highlightDocx(
  container: HTMLElement,
  paragraph: string,
  snippet?: string,
  { exactOnly = false }: { exactOnly?: boolean } = {}
) {
  clearHighlights(container);
  const textmap = unveilTextmap(container);

  const regex = contextToRegex(paragraph);

  const matches = regex.exec(textmap.content);

  // If snippet is provided and paragraph doesn't match, try to match snippet directly
  // with context-based relevance scoring
  if (!matches && snippet) {
    const snippetRegex = contextToRegex(snippet);
    const allMatches: RegExpExecArray[] = [];
    let match: RegExpExecArray | null;

    // Reset regex lastIndex to find all matches
    snippetRegex.lastIndex = 0;
    while ((match = snippetRegex.exec(textmap.content)) !== null) {
      allMatches.push(match);
    }

    console.log("snippet direct matches found: ", allMatches.length);

    if (allMatches.length === 0) {
      return false;
    }

    // If only one match, use it
    if (allMatches.length === 1) {
      const snippetMatch = allMatches[0];
      return highlightSnippetMatch(snippetMatch, textmap, snippet);
    }

    // Multiple matches - find the most relevant one using context similarity
    const bestMatch = findBestContextMatch(
      allMatches,
      textmap.content,
      paragraph,
      snippet
    );

    if (bestMatch) {
      return highlightSnippetMatch(bestMatch, textmap, snippet);
    }

    return false;
  }

  if (!matches) {
    return false;
  }

  const startIndex = matches.index;
  const endIndex = startIndex + matches[0].length;
  const elements = textmap.elementsBetween(startIndex, endIndex);

  if (snippet) {
    // This part handles highlighting the specific snippet
    const snippetTextmap = makeTextmap(elements);
    const snippetRegex = contextToRegex(snippet);
    const snippetMatches = snippetRegex.exec(snippetTextmap.content);

    if (snippetMatches) {
      const snippetStartIndex = snippetMatches.index;
      const snippetEndIndex = snippetStartIndex + snippetMatches[0].length;
      const snippetElements = snippetTextmap.elementsBetween(
        snippetStartIndex,
        snippetEndIndex
      );
      let marks = [];
      for (const element of snippetElements) {
        const mark = highlightPartialTextNode(element, snippet);
        if (mark) {
          marks.push(mark);
        }
      }
      if (marks[0]) {
        marks[0].scrollIntoView({
          behavior: "smooth",
        });
        return true;
      }
    }
  }

  if (exactOnly) return false;

  // If highlighting the whole paragraph match
  for (const element of elements) {
    triggerHighlight(element);
  }

  if (elements[0]) {
    elements[0].parentElement?.scrollIntoView({ behavior: "smooth" });
    return true;
  }

  return false;
}

/**
 * Highlights a snippet match found in the document
 */
function highlightSnippetMatch(
  match: RegExpExecArray,
  textmap: Textmap,
  snippet: string
): boolean {
  const snippetStartIndex = match.index;
  const snippetEndIndex = snippetStartIndex + match[0].length;
  const snippetElements = textmap.elementsBetween(
    snippetStartIndex,
    snippetEndIndex
  );
  let marks = [];
  for (const element of snippetElements) {
    const mark = highlightPartialTextNode(element, snippet);
    if (mark) {
      marks.push(mark);
    }
  }
  if (marks[0]) {
    marks[0].scrollIntoView({
      behavior: "smooth",
    });
    return true;
  }
  return false;
}

/**
 * Finds the best matching snippet by comparing context similarity
 */
function findBestContextMatch(
  matches: RegExpExecArray[],
  documentContent: string,
  paragraph: string,
  snippet: string
): RegExpExecArray | null {
  const CONTEXT_WINDOW = 500; // Characters before and after the match to consider

  let bestMatch: RegExpExecArray | null = null;
  let bestScore = -1;

  // Extract meaningful words from paragraph for comparison
  const paragraphWords = extractMeaningfulWords(paragraph);

  for (const match of matches) {
    const matchIndex = match.index;
    const contextStart = Math.max(0, matchIndex - CONTEXT_WINDOW);
    const contextEnd = Math.min(
      documentContent.length,
      matchIndex + match[0].length + CONTEXT_WINDOW
    );
    const context = documentContent.substring(contextStart, contextEnd);

    const similarity = calculateContextSimilarity(context, paragraphWords);
    console.log(`Match at index ${matchIndex}, similarity: ${similarity}`);

    if (similarity > bestScore) {
      bestScore = similarity;
      bestMatch = match;
    }
  }

  console.log(`Best match selected with similarity: ${bestScore}`);
  return bestMatch;
}

/**
 * Extracts meaningful words from text (filters out common words and very short words)
 */
function extractMeaningfulWords(text: string): Set<string> {
  // Remove markdown formatting
  const cleanText = text
    .replace(/[#*|`\-_]/g, " ") // Remove markdown chars
    .replace(/\s+/g, " ") // Normalize whitespace
    .toLowerCase();

  // Common stop words to ignore
  const stopWords = new Set([
    "the",
    "a",
    "an",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "of",
    "with",
    "by",
    "from",
    "as",
    "is",
    "was",
    "are",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "do",
    "does",
    "did",
    "will",
    "would",
    "could",
    "should",
    "may",
    "might",
    "can",
    "this",
    "that",
    "these",
    "those",
    "it",
    "its",
    "they",
    "them",
    "their",
    "we",
    "our",
    "you",
    "your",
    "i",
    "me",
    "my",
    "he",
    "she",
    "his",
    "her",
  ]);

  const words = cleanText
    .split(/\s+/)
    .filter((word) => word.length > 2 && !stopWords.has(word))
    .map((word) => word.replace(/[^\w]/g, "")) // Remove punctuation
    .filter((word) => word.length > 0);

  return new Set(words);
}

/**
 * Calculates similarity between context and paragraph words
 * Returns a score between 0 and 1, where 1 is perfect match
 */
function calculateContextSimilarity(
  context: string,
  paragraphWords: Set<string>
): number {
  if (paragraphWords.size === 0) {
    return 0;
  }

  const contextWords = extractMeaningfulWords(context);

  // Count how many paragraph words appear in the context
  let matches = 0;
  for (const word of paragraphWords) {
    if (contextWords.has(word)) {
      matches++;
    }
  }

  // Return the ratio of matching words
  return matches / paragraphWords.size;
}

/**
 * Highlights only a specific part of a text node by splitting it
 * @param textNode The text node containing the content to partially highlight
 * @param textToHighlight The exact text to highlight within the node
 */
function highlightPartialTextNode(textNode: Node, textToHighlight: string) {
  if (textNode.nodeType !== Node.TEXT_NODE) {
    // If it's not a text node, use the original method
    triggerHighlight(textNode);
    return;
  }

  const parent = textNode.parentNode;
  if (!parent) return;

  const content = textNode.textContent || "";

  // Find the exact match position
  const regex = contextToRegex(textToHighlight);
  const match = regex.exec(content);

  if (!match) {
    // If no match found in this specific text node, don't highlight
    return;
  }

  const startPos = match.index;
  const endPos = startPos + match[0].length;

  // Split the text node into three parts:
  // 1. Text before the highlight
  // 2. Text to highlight
  // 3. Text after the highlight
  const beforeText = content.substring(0, startPos);
  const highlightText = content.substring(startPos, endPos);
  const afterText = content.substring(endPos);

  // Create the DOM nodes
  const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
  const highlightNode = document.createTextNode(highlightText);
  const afterNode = afterText ? document.createTextNode(afterText) : null;

  // Create the highlight element
  const mark = document.createElement("mark");
  mark.classList.add("fp-dark-highlight");
  mark.appendChild(highlightNode);

  // Replace the original text node with our three parts
  if (beforeNode) {
    parent.insertBefore(beforeNode, textNode);
  }

  parent.insertBefore(mark, textNode);

  if (afterNode) {
    parent.insertBefore(afterNode, textNode);
  }

  // Remove the original text node
  parent.removeChild(textNode);

  return mark;
}
/**
 * Turn a free-form context string into a RegExp that tolerates
 * any whitespace (space, newline, tab) by collapsing each run to \s+.
 */
function contextToRegex(context: string, exact: boolean = false): RegExp {
  // 1. Trim out leading/trailing whitespace
  const trimmed = context.trim();

  // 2. Escape all regex-special chars so we match text literally
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // 3. Replace any run of whitespace with \s+ so we match linebreaks, tabs, etc.
  const pattern = escaped.replace(/\s+/g, "\\s*");

  // 4. Create a case-insensitive, global regex
  return new RegExp(pattern, "gi");
}

export function triggerHighlight(node: Node, element: string = "mark") {
  // Ensure the node exists in the document
  const parent = node.parentNode;
  if (!parent) {
    // Nothing to do if the node has no parent
    return;
  }

  // Create a <mark> element
  const mark = document.createElement(element);
  mark.classList.add("fp-dark-highlight");

  // Insert the <mark> just before the target node
  parent.insertBefore(mark, node);

  // Move the target node into the <mark>
  mark.appendChild(node);
}

export function clearHighlights(container: HTMLElement) {
  let mark: Element | null = null;
  while ((mark = container.querySelector(".fp-dark-highlight"))) {
    const parent = mark.parentNode;
    if (!parent) return;

    // Move each child of the <mark> back into the DOM at the mark's position
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }

    // Remove the now-empty <mark> element
    parent.removeChild(mark);
  }
}

/**
 * Iterates through the container, pulling together all the text inside, creating
 * a mapping back to the original nodes.
 */
function unveilTextmap(root: HTMLElement) {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT, // only show element nodes
    null // no custom filter
  );

  // TODO: Determine a better datastructure and use it here
  let content = "";
  const map: IndexToElement[] = [];

  let node;
  while ((node = walker.nextNode())) {
    const startPos = content.length;
    content += node.textContent;

    map.push({ index: startPos, node });
  }

  return new Textmap(content, map);
}

function makeTextmap(nodes: Node[]) {
  // TODO: Determine a better datastructure and use it here
  let content = "";
  const map: IndexToElement[] = [];

  for (const node of nodes) {
    const startPos = content.length;
    content += node.textContent;

    map.push({ index: startPos, node });
  }

  return new Textmap(content, map);
}

interface IndexToElement {
  index: number;
  node: Node;
}

class Textmap {
  constructor(
    public readonly content: string,
    private readonly map: IndexToElement[]
  ) {}

  elementAtIndex(index: number) {
    let node: Node | null = null;
    for (const item of this.map) {
      if (!(index >= item.index)) {
        break;
      }
      node = item.node;
    }
    return node;
  }

  elementsBetween(start: number, stop: number) {
    // TODO: Optimize
    const nodes: Node[] = [];
    for (let i = start; i < stop; i++) {
      let node = this.elementAtIndex(i);
      if (!node) continue;
      nodes.push(node);
    }

    for (let i = 1; i < nodes.length; ) {
      if (nodes[i - 1] === nodes[i]) {
        nodes.splice(i, 1);
      } else {
        i++;
      }
    }

    return nodes;
  }
}
